import { ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import classNames from 'classnames';
import { Show } from 'components/shared/Show';
import React from 'react';
import { IDisburseAllDisbursementData } from 'types/order/escrow/IDisburseAllDisbursementData';

export interface IDisbursementTableProps {
  data: IDisburseAllDisbursementData[];
  columns: ColumnDef<IDisburseAllDisbursementData, any>[];
  header: string;
  selectedId?: string;
  setSelectedDisbursement?: (x: IDisburseAllDisbursementData) => void;
}

export const DisbursementTable = ({
  data,
  columns,
  header,
  selectedId,
  setSelectedDisbursement,
}: IDisbursementTableProps) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="mt-2 flow-root basis-full">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <table className={'min-w-full divide-y divide-gray-300'}>
              <thead className={'bg-gray-50'}>
                <tr>
                  <th
                    colSpan={columns.length}
                    className="text-white-700 shadow-inset border-b border-gray-300 bg-white px-5 py-2 text-left text-sm font-semibold"
                  >
                    {header}
                  </th>
                </tr>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={'select-none p-2 text-left text-sm font-thin text-gray-500'}
                        style={{ width: header.getSize() ? header.getSize() : 'auto' }}
                      >
                        <Show when={!header.isPlaceholder}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </Show>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className={'divide-y divide-gray-300 bg-white'}>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className={classNames(
                      selectedId === row.original.disbursement.id
                        ? 'bg-blue-200'
                        : 'odd:bg-gray-100 even:bg-white hover:bg-blue-100',
                      !!row.original.fee.linkedDocumentIds.length && !!setSelectedDisbursement ? 'cursor-pointer' : ''
                    )}
                    onClick={() =>
                      !!row.original.fee.linkedDocumentIds.length && setSelectedDisbursement?.(row.original)
                    }
                    title={
                      !!row.original.fee.linkedDocumentIds.length && setSelectedDisbursement ? 'View Documents' : ''
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        style={{ width: cell.column.getSize() ? cell.column.getSize() : 'auto' }}
                        key={cell.id}
                        className={'whitespace-pre-line p-2 text-sm font-medium text-gray-900 '}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
